import React from 'react';
import { Input } from '../../../components/ui/Input/Input';
import { Select } from '../../../components/ui/Select/Select';
import { unformat } from '../../../utils/formatters';
import { useFieldFormatters } from '../../../hooks/useFieldFormatters';
import { AddressHeader } from './AddressHeader';
import { CommercialToggle } from './CommercialToggle';
import { CorrespondenceSection } from './CorrespondenceSection';
import { CepOpcoes } from './CepOpcoes';
import { FIELD_NAMES, PHONE_FIELDS } from './addressConstants';
import { Section, Row } from './addressStyles';

// Seção completa de endereço (residencial ou comercial): busca de CEP,
// seleção de estado/município, telefones e correspondência.
// Residencial tem campos obrigatórios; comercial só fica obrigatório
// quando o usuário optar por correspondência comercial ("S").
/**
 * @param {string} [props.type='residencial'] - 'residencial' | 'comercial'
 * @param {Object} props.values - Valores do formulário
 * @param {Object} props.errors - Erros de validação
 * @param {Object} props.touched - Campos já tocados
 * @param {Function} props.onChange - Callback de mudança de campo
 * @param {Function} props.onBlur - Callback de blur
 * @param {Function} props.onCEPBlur - Callback de blur do CEP (dispara busca)
 * @param {Array} [props.cepOpcoes] - Registros do CEP quando ele tem mais de um endereço
 * @param {Function} [props.onCepOpcaoSelect] - Recebe o registro escolhido em CepOpcoes
 * @param {Object} props.opcoes - Opções de estados, municípios e países
 * @param {boolean} props.showCommercial - Exibe os campos comerciais (type='comercial')
 * @param {Function} props.onToggleCommercial - Callback do checkbox comercial
 * @param {Function} props.onCorrespondenciaChange - Callback dos rádios de correspondência
 */
export const AddressSection = ({
  type = 'residencial',
  values,
  errors,
  touched,
  onChange,
  onBlur,
  onCEPBlur,
  cepOpcoes = [],
  onCepOpcaoSelect,
  opcoes,
  showCommercial,
  onToggleCommercial,
  onCorrespondenciaChange
}) => {
  const isCommercial = type === 'comercial';
  const names = FIELD_NAMES[type];
  const { handleCEPChange, handlePhoneChange } = useFieldFormatters(onChange);
  const phoneFields = PHONE_FIELDS[type];

  const isCommercialRequired = isCommercial && values.correspondenciaComercial === 'S';

  // Comercial não marcado: mostra só o cabeçalho e o toggle para ativar
  if (isCommercial && !showCommercial) {
    return (
      <Section>
        <AddressHeader type={type} />
        <CommercialToggle
          showCommercial={showCommercial}
          onToggle={onToggleCommercial}
        />
      </Section>
    );
  }

  return (
    <Section>
      <AddressHeader type={type} />

      {isCommercial && (
        <CommercialToggle
          showCommercial={showCommercial}
          onToggle={onToggleCommercial}
        />
      )}

      <Input
        label="CEP"
        name={names.cep}
        value={values[names.cep] || ''}
        onChange={handleCEPChange}
        onBlur={(e) => onCEPBlur(e, type)}
        error={touched[names.cep] && errors[names.cep]}
        required={isCommercial ? isCommercialRequired : !isCommercial}
        placeholder="00000-000"
        maxLength={9}
        helper={!isCommercial && !errors[names.cep] &&
          values[names.cep] && unformat(values[names.cep]).length === 8 &&
          'Digite e saia do campo para buscar'}
      />

      <CepOpcoes
        name={names.cep}
        opcoes={cepOpcoes}
        selecionadoId={values[`${names.cep}Id`]}
        onSelect={onCepOpcaoSelect}
      />

      <Input
        label="Logradouro"
        name={names.logradouro}
        value={values[names.logradouro] || ''}
        onChange={onChange}
        onBlur={onBlur}
        error={touched[names.logradouro] && errors[names.logradouro]}
        required={isCommercial ? isCommercialRequired : !isCommercial}
        placeholder="Rua, Avenida, etc."
        disabled
        helper="Preenchido automaticamente pela busca de CEP"
      />

      <Row>
        <Input
          label="Número"
          name={names.numero}
          value={values[names.numero] || ''}
          onChange={onChange}
          onBlur={onBlur}
          error={touched[names.numero] && errors[names.numero]}
          required={isCommercial ? isCommercialRequired : !isCommercial}
          placeholder="123"
        />

        <Input
          label="Complemento"
          name={names.complemento}
          value={values[names.complemento] || ''}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Apto, Bloco, etc."
        />
      </Row>

      <Input
        label="Bairro"
        name={names.bairro}
        value={values[names.bairro] || ''}
        onChange={onChange}
        onBlur={onBlur}
        error={touched[names.bairro] && errors[names.bairro]}
        required={isCommercial ? isCommercialRequired : !isCommercial}
        placeholder="Bairro"
        disabled
      />

      <Select
        label="Estado"
        name={names.estado}
        value={values[names.estado] || ''}
        onChange={onChange}
        error={touched[names.estado] && errors[names.estado]}
        required={isCommercial ? isCommercialRequired : !isCommercial}
        disabled
      >
        <option value="">
          {opcoes.loading ? 'Carregando...' : 'Preenchido automaticamente pela busca de CEP'}
        </option>
        {opcoes.estados.map(estado => (
          <option key={estado.id} value={estado.id}>
            {estado.sigla} - {estado.nome}
          </option>
        ))}
      </Select>

      <Select
        label="Município"
        name={names.municipio}
        value={values[names.municipio] || ''}
        onChange={onChange}
        error={touched[names.municipio] && errors[names.municipio]}
        required={isCommercial ? isCommercialRequired : !isCommercial}
        disabled
      >
        <option value="">Preenchido automaticamente pela busca de CEP</option>

        {opcoes.municipios.map((municipio) => (
          <option
            key={municipio.id}
            value={municipio.id}
          >
            {municipio.nome}
          </option>
        ))}
      </Select>

      <Select
        label="País"
        name={names.pais}
        value={values[names.pais] || '1'}
        onChange={onChange}
        disabled
      >
        {opcoes.paises.map(pais => (
          <option key={pais.id} value={pais.id}>
            {pais.nome} {pais.sigla ? `(${pais.sigla})` : ''}
          </option>
        ))}
      </Select>

      {/* Residencial: sempre obrigatório. Comercial: só quando é o endereço de correspondência */}
      <Input
        label="E-mail"
        type="email"
        name={names.email}
        value={values[names.email] || ''}
        onChange={onChange}
        onBlur={onBlur}
        error={touched[names.email] && errors[names.email]}
        required={isCommercial ? isCommercialRequired : true}
        placeholder={isCommercial ? "comercial@empresa.com" : "seu@email.com"}
      />

      {phoneFields.map(field => (
        <Input
          key={field.name}
          label={field.label}
          name={isCommercial ? names[field.name] : field.name}
          value={isCommercial ? values[names[field.name]] : values[field.name]}
          onChange={handlePhoneChange}
          onBlur={onBlur}
          error={touched[isCommercial ? names[field.name] : field.name] &&
            errors[isCommercial ? names[field.name] : field.name]}
          required={field.required}
          placeholder={field.placeholder}
          maxLength={15}
        />
      ))}

      <CorrespondenceSection
        type={type}
        values={values}
        names={names}
        onCorrespondenciaChange={onCorrespondenciaChange}
        isResidencialCorrespondenceSelected={values.correspondenciaResidencial === 'S'}
      />
    </Section>
  );
};